/**
 * ============================================================================
 * PROOF OF CONCEPT - Deal Page with Left-Side Notepad
 * ============================================================================
 * 
 * This is a non-destructive POC to visualize the "Deal Notepad" concept.
 * Navigate to /crm/deals/:dealId/notepad-poc to see it in action.
 * 
 * CONCEPT: 3-column layout
 *   [Notepad (left)]  [Activity/Tasks (center)]  [Deal Details (right)]
 * 
 * The notepad is a persistent, ever-growing scratchpad that:
 *   - Auto-saves as you type (debounced)
 *   - Auto-appends timestamped entries when activities are logged
 *   - Fills the entire left column height
 *   - Can be collapsed to give more room to the center column
 * 
 * WHAT WOULD NEED TO HAPPEN TO SHIP THIS:
 *   1. Add `scratchpad TEXT` column to crm_deals table
 *   2. Update transformDealToCamelCase to include scratchpad
 *   3. Wire auto-append into handleSubmit (activity log)
 *   4. Replace current DealPage layout grid with 3-column version
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  IconButton,
  Button,
  Divider,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  Grid,
  GridItem,
  Tooltip,
  Avatar,
  Icon,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  StatLabel,
  StatNumber,
  Textarea,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiPhone,
  FiMail,
  FiEdit2,
  FiCheckCircle,
  FiMessageSquare,
  FiCalendar,
  FiFileText,
  FiSave,
  FiClock,
  FiMaximize2,
  FiMinimize2,
  FiCopy,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import { supabase, isSupabaseConfigured, transformDealToCamelCase, transformContactToCamelCase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import DealNotepad from '../../components/CRM/DealNotepad';
import DealTimeline from '../../components/CRM/DealTimeline';
import moment from 'moment';


// ─── MOCK DATA (for when deal hasn't loaded yet or for pure visual testing) ──
const MOCK_NOTEPAD_CONTENT = `Called John - he's interested in refinancing. Current rate is 6.75%, looking to get below 6%.

--- Activity Logged [02/08/26 2:15 PM] ---
Disposition: Spoke With - Interested
Next Step: Send pre-qual letter
Notes: Confirmed income docs needed. Will email checklist.

--- Activity Logged [02/09/26 10:30 AM] ---
Disposition: Follow-up Call
Notes: Left voicemail, will try again tomorrow. Need to discuss appraisal timeline.

Quick notes:
- Property is a 3BR/2BA in Scottsdale  
- Purchase price ~$485K
- He mentioned his wife wants to be on the loan as co-borrower
- Prefers to close by end of March
- Realtor is Sarah from Keller Williams (480-555-0199)

TODO for this deal:
☐ Send pre-qual letter
☐ Get income docs from borrower  
☐ Confirm co-borrower SSN
☐ Follow up on appraisal order`;


export default function DealPageNotepadPOC() {
  const { dealId } = useParams();
  const history = useHistory();
  const { currentUser } = useAuth();
  const { dealStages, dispositions, nextActions } = useCrmSettings();
  const toast = useToast();
  const notepadRef = useRef(null);

  const STAGES = dealStages || [];

  // ─── State ────────────────────────────────────────────────────────────
  const [deal, setDeal] = useState(null);
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notepadContent, setNotepadContent] = useState(MOCK_NOTEPAD_CONTENT);
  const [notepadCollapsed, setNotepadCollapsed] = useState(false);

  // ─── Colors ───────────────────────────────────────────────────────────
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const cardBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const pocBannerBg = useColorModeValue('purple.50', 'purple.900');
  const pocBannerBorder = useColorModeValue('purple.200', 'purple.700');

  // ─── Load Real Data ──────────────────────────────────────────────────
  useEffect(() => {
    if (!dealId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    const fetchDeal = async () => {
      const { data, error } = await supabase
        .from('crm_deals')
        .select('*')
        .eq('id', dealId)
        .single();

      if (error || !data) {
        console.error('Error loading deal for POC:', error);
        setLoading(false);
        return;
      }

      const transformedDeal = transformDealToCamelCase(data);
      setDeal(transformedDeal);
      
      // Load notepad content from deal if it exists
      if (data.scratchpad) {
        setNotepadContent(data.scratchpad);
      }

      // Load contact
      if (data.contact_id) {
        const { data: contactData } = await supabase
          .from('crm_contacts')
          .select('*')
          .eq('id', data.contact_id)
          .single();
        
        if (contactData) {
          setContact(transformContactToCamelCase(contactData));
        }
      }

      // Load activities
      const { data: activityData } = await supabase
        .from('crm_activities')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (activityData) {
        setActivities(activityData.map(a => ({
          id: a.id,
          text: a.content,
          authorId: a.author_id,
          timestamp: a.created_at,
          activityType: a.type,
          ...(a.metadata || {}),
        })));
      }

      setLoading(false);
    };

    fetchDeal();
  }, [dealId]);

  // ─── Simulated Activity Log (for POC demo) ────────────────────────────
  const handleDemoLogActivity = () => {
    const demoNote = 'Demo activity logged - spoke with borrower about docs.';
    const timestamp = moment().format('MM/DD/YY h:mm A');
    const entry = `\n\n--- Activity Logged [${timestamp}] ---\nDisposition: Spoke With - Interested\nNotes: ${demoNote}`;
    setNotepadContent(prev => prev + entry);
    toast({ title: 'Demo: Activity auto-appended to notepad', status: 'info', duration: 3000 });
  };

  // ─── Loading State ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box bg={bgColor} minH="100vh">
        <Container maxW="full" py={8}>
          <Center h="60vh" flexDir="column">
            <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
            <Text color="gray.600">Loading POC...</Text>
          </Center>
        </Container>
      </Box>
    );
  }

  const contactName = contact 
    ? `${contact.firstName} ${contact.lastName}` 
    : 'John Smith (Mock)';
  const dealStage = deal?.stage || 'pre-qual';
  const stageInfo = STAGES.find(s => s.value === dealStage) || { label: 'Pre-Qual', color: 'blue' };

  // ─── RENDER ───────────────────────────────────────────────────────────
  return (
    <Box bg={bgColor} minH="100vh" pb={8} overflowX="hidden">
      
      {/* POC Banner */}
      <Box
        bg={pocBannerBg}
        borderBottomWidth="2px"
        borderColor={pocBannerBorder}
        px={6}
        py={3}
      >
        <HStack justify="space-between" maxW="full" mx="auto">
          <HStack spacing={3}>
            <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>POC</Badge>
            <Text fontSize="sm" fontWeight="semibold" color="purple.700">
              Deal Page with Left-Side Notepad — Proof of Concept
            </Text>
          </HStack>
          <HStack spacing={3}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={handleDemoLogActivity}
            >
              Demo: Log Activity (auto-appends)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              onClick={() => history.push(`/crm/deals/${dealId}`)}
            >
              Back to Real Deal Page
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Sticky Header (simplified version of real header) */}
      <Box
        bg={headerBG}
        py={4}
        px={{ base: 4, md: 6, lg: 8 }}
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={100}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <HStack justify="space-between" maxW="full" mx="auto">
          <HStack spacing={3}>
            <Tooltip label="Back">
              <IconButton icon={<FiArrowLeft />} aria-label="Back" onClick={() => history.goBack()} variant="ghost" size="md" />
            </Tooltip>
            <Avatar size="md" name={contactName} />
            <VStack align="flex-start" spacing={1}>
              <HStack>
                <Text fontWeight="bold" fontSize="lg">{contactName}</Text>
                <Badge colorScheme={stageInfo.color} fontSize="sm" px={3} py={1}>
                  {stageInfo.label}
                </Badge>
              </HStack>
              <HStack spacing={4}>
                {contact?.email && (
                  <HStack spacing={1}>
                    <Icon as={FiMail} boxSize={3} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">{contact.email}</Text>
                  </HStack>
                )}
                {contact?.phone && (
                  <HStack spacing={1}>
                    <Icon as={FiPhone} boxSize={3} color="gray.500" />
                    <Text fontSize="sm" color="gray.600">{contact.phone}</Text>
                  </HStack>
                )}
              </HStack>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            {deal?.tags?.length > 0 && (
              <Wrap spacing={1}>
                {deal.tags.slice(0, 3).map(tag => (
                  <WrapItem key={tag}>
                    <Tag size="sm" colorScheme="blue" variant="subtle">
                      <TagLabel>{tag}</TagLabel>
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
            <Button size="sm" leftIcon={<FiEdit2 />} variant="outline">Edit</Button>
          </HStack>
        </HStack>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════
          MAIN 3-COLUMN LAYOUT — This is the core of the POC
          ═══════════════════════════════════════════════════════════════════ */}
      <Box px={{ base: 4, md: 6 }} py={6} maxW="full" mx="auto">
        <Grid
          templateColumns={{
            base: '1fr',                                         // Mobile: stacked
            md: notepadCollapsed ? '48px 1fr' : '1fr 1fr',      // Tablet: 2-col
            xl: notepadCollapsed 
              ? '48px 1.8fr 1.2fr'                               // Desktop collapsed: thin + center + right
              : '1fr 1.4fr 1fr',                                 // Desktop: notepad + center + right
          }}
          gap={6}
          alignItems="start"
        >
          
          {/* ── COLUMN 1: NOTEPAD (Left Side) ─────────────────────────── */}
          <GridItem
            position={{ base: 'relative', xl: 'sticky' }}
            top={{ base: 'auto', xl: '85px' }}
            alignSelf="start"
          >
            {notepadCollapsed ? (
              // Collapsed: Just a thin strip with expand button
              <Box
                bg={cardBG}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                shadow="sm"
                py={4}
                display="flex"
                flexDirection="column"
                alignItems="center"
                h="calc(100vh - 200px)"
              >
                <Tooltip label="Expand Notepad" placement="right">
                  <IconButton
                    icon={<FiChevronRight />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setNotepadCollapsed(false)}
                    aria-label="Expand notepad"
                    mb={3}
                  />
                </Tooltip>
                <Icon as={FiFileText} color="blue.400" boxSize={5} mb={2} />
                <Text
                  fontSize="xs"
                  color="gray.500"
                  sx={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                  Notepad
                </Text>
              </Box>
            ) : (
              // Expanded: Full notepad
              <Box position="relative">
                {/* Collapse button overlaid on top-right */}
                <Tooltip label="Collapse Notepad" placement="right">
                  <IconButton
                    icon={<FiChevronLeft />}
                    size="xs"
                    variant="ghost"
                    position="absolute"
                    top={2}
                    right={2}
                    zIndex={10}
                    onClick={() => setNotepadCollapsed(true)}
                    aria-label="Collapse notepad"
                  />
                </Tooltip>
                <DealNotepad
                  dealId={dealId}
                  initialContent={notepadContent}
                  onContentChange={setNotepadContent}
                  maxHeight="calc(100vh - 200px)"
                  minHeight="400px"
                />
              </Box>
            )}
          </GridItem>

          {/* ── COLUMN 2: ACTIVITY / TASKS (Center) ───────────────────── */}
          <GridItem>
            <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
              <Tabs colorScheme="blue">
                <TabList px={6}>
                  <Tab>
                    <HStack spacing={2}>
                      <Icon as={FiCheckCircle} />
                      <Text>Activity & Update</Text>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack spacing={2}>
                      <Icon as={FiCalendar} />
                      <Text>Tasks</Text>
                      <Badge ml={2} colorScheme="blue">2</Badge>
                    </HStack>
                  </Tab>
                </TabList>
                <TabPanels>
                  {/* Activity Tab */}
                  <TabPanel px={6} py={6}>
                    <VStack spacing={6} align="stretch">
                      <Heading size="md" mb={2}>Log Activity</Heading>
                      
                      {/* Simplified activity form for POC */}
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="flex-start" spacing={1}>
                          <Text fontSize="sm" fontWeight="semibold">POC Note</Text>
                          <Text fontSize="xs">
                            The full activity form (stage, disposition, next step, due date, notes) 
                            lives here — same as the current DealPage. When you click "Save & Log Activity", 
                            the entry auto-appends to the notepad on the left.
                          </Text>
                        </VStack>
                      </Alert>
                      
                      <FormControl>
                        <FormLabel fontSize="sm">Quick Note</FormLabel>
                        <Textarea placeholder="Log call details, outcomes, or notes..." rows={4} />
                      </FormControl>
                      
                      <Button 
                        colorScheme="blue" 
                        size="lg" 
                        rightIcon={<FiCheckCircle />}
                        onClick={handleDemoLogActivity}
                      >
                        Save & Log Activity
                      </Button>

                      <Divider my={4} />
                      
                      <Heading size="md" mb={2}>Activity Timeline</Heading>
                      {activities.length > 0 ? (
                        <DealTimeline activities={activities} maxHeight="400px" />
                      ) : (
                        <Box py={8} textAlign="center">
                          <Text color="gray.500" fontSize="sm">
                            Activity timeline will appear here (same as current DealPage)
                          </Text>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Tasks Tab (placeholder) */}
                  <TabPanel px={6} py={6}>
                    <Center py={12}>
                      <VStack>
                        <Icon as={FiCalendar} boxSize={12} color="gray.300" mb={3} />
                        <Text color="gray.500" fontSize="sm">Tasks tab (same as current DealPage)</Text>
                      </VStack>
                    </Center>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </GridItem>

          {/* ── COLUMN 3: DEAL DETAILS (Right Sidebar) ────────────────── */}
          <GridItem
            position={{ base: 'relative', xl: 'sticky' }}
            top={{ base: 'auto', xl: '85px' }}
            alignSelf="start"
            maxHeight={{ base: 'none', xl: 'calc(100vh - 150px)' }}
            overflowY={{ base: 'visible', xl: 'auto' }}
            display={{ base: 'none', md: 'none', xl: 'block' }}
          >
            <VStack align="stretch" spacing={6}>
              
              {/* Quick Actions */}
              <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm" p={4}>
                <VStack align="stretch" spacing={2}>
                  <Heading size="sm" mb={1}>Quick Actions</Heading>
                  <Divider />
                  <Button leftIcon={<FiPhone />} size="sm" colorScheme="blue" w="full">Call</Button>
                  <Button leftIcon={<FiMessageSquare />} size="sm" colorScheme="green" w="full">Text</Button>
                  <Button leftIcon={<FiMail />} size="sm" colorScheme="purple" w="full">Email</Button>
                </VStack>
              </Box>

              {/* Deal Vitals */}
              <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm" p={5}>
                <VStack align="stretch" spacing={4}>
                  <Heading size="sm">Deal Vitals</Heading>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <FormControl>
                      <StatLabel fontSize="xs" mb={1}>Loan Amount</StatLabel>
                      <StatNumber fontSize="lg" color="green.600">
                        {deal?.amount ? `$${deal.amount.toLocaleString()}` : '$485,000'}
                      </StatNumber>
                    </FormControl>
                    <FormControl>
                      <StatLabel fontSize="xs" mb={1}>Est. Closing</StatLabel>
                      <StatNumber fontSize="lg">
                        {deal?.estimatedClosingDate 
                          ? moment(deal.estimatedClosingDate).format('MM/DD/YY') 
                          : '03/31/26'}
                      </StatNumber>
                    </FormControl>
                    <FormControl>
                      <StatLabel fontSize="xs" mb={1}>Product</StatLabel>
                      <StatNumber fontSize="md">{deal?.product || 'Conventional'}</StatNumber>
                    </FormControl>
                    <FormControl>
                      <StatLabel fontSize="xs" mb={1}>Source</StatLabel>
                      <StatNumber fontSize="md" textTransform="capitalize">{deal?.source || 'Google'}</StatNumber>
                    </FormControl>
                  </Grid>
                </VStack>
              </Box>

              {/* Borrower Details (simplified) */}
              <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm" p={5}>
                <VStack align="stretch" spacing={3}>
                  <Heading size="sm">Borrower Details</Heading>
                  <Divider />
                  <HStack>
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.500">First Name</FormLabel>
                      <Text fontSize="sm">{contact?.firstName || 'John'}</Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.500">Last Name</FormLabel>
                      <Text fontSize="sm">{contact?.lastName || 'Smith'}</Text>
                    </FormControl>
                  </HStack>
                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">Property Address</FormLabel>
                    <Text fontSize="sm">
                      {deal?.propertyAddress?.street 
                        ? `${deal.propertyAddress.street}, ${deal.propertyAddress.city}, ${deal.propertyAddress.state} ${deal.propertyAddress.zip}`
                        : '1234 Scottsdale Rd, Scottsdale, AZ 85251'}
                    </Text>
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </GridItem>
        </Grid>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════
          CONCEPT EXPLANATION (at the bottom)
          ═══════════════════════════════════════════════════════════════════ */}
      <Container maxW="container.lg" mt={8}>
        <Box
          bg={pocBannerBg}
          borderWidth="2px"
          borderColor={pocBannerBorder}
          borderRadius="xl"
          p={6}
        >
          <VStack align="stretch" spacing={4}>
            <Heading size="md" color="purple.700">How This Would Work</Heading>
            <Divider borderColor="purple.200" />
            
            <VStack align="stretch" spacing={3}>
              <HStack align="flex-start" spacing={3}>
                <Badge colorScheme="green" mt={1}>1</Badge>
                <Text fontSize="sm">
                  <strong>Auto-saves as you type</strong> — Content saves to a new `scratchpad` column on the deal record 
                  with a 1.5-second debounce. No "save" button needed (though one exists for peace of mind).
                </Text>
              </HStack>
              
              <HStack align="flex-start" spacing={3}>
                <Badge colorScheme="green" mt={1}>2</Badge>
                <Text fontSize="sm">
                  <strong>Auto-appends on activity log</strong> — When a user clicks "Save & Log Activity", a timestamped 
                  summary (disposition, notes, stage change) auto-appends to the notepad. Try the purple "Demo" button above!
                </Text>
              </HStack>
              
              <HStack align="flex-start" spacing={3}>
                <Badge colorScheme="green" mt={1}>3</Badge>
                <Text fontSize="sm">
                  <strong>Collapsible</strong> — Click the chevron on the notepad to collapse it into a thin strip, 
                  giving the center column more room. Expand anytime.
                </Text>
              </HStack>
              
              <HStack align="flex-start" spacing={3}>
                <Badge colorScheme="green" mt={1}>4</Badge>
                <Text fontSize="sm">
                  <strong>Keyboard shortcuts</strong> — Cmd/Ctrl+S for manual save. Standard text editing applies.
                </Text>
              </HStack>
              
              <HStack align="flex-start" spacing={3}>
                <Badge colorScheme="green" mt={1}>5</Badge>
                <Text fontSize="sm">
                  <strong>Copy & clear</strong> — Copy entire notepad to clipboard or clear it. Word/char counts shown in the toolbar.
                </Text>
              </HStack>
              
              <HStack align="flex-start" spacing={3}>
                <Badge colorScheme="blue" mt={1}>DB</Badge>
                <Text fontSize="sm">
                  <strong>Database change needed:</strong> One migration to add a `scratchpad TEXT` column to `crm_deals`. 
                  No new tables, no new RLS policies — just one column.
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}
